import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Classification {
  level: string
  datasets: number
  tables: number
  columns: number
  color: string
}

interface SensitiveData {
  id: string
  dataset: string
  column: string
  type: string
  sensitivity: string
  encryption: boolean
  masking: boolean
}

interface Statistics {
  total_classified: number
  unclassified: number
  classification_rate: number
  pii_datasets: number
  sensitive_datasets: number
  policy_violations: number
}

export default function DataClassificationPage() {
  const [classifications, setClassifications] = useState<Classification[]>([])
  const [sensitiveData, setSensitiveData] = useState<SensitiveData[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [classRes, sensitiveRes, statsRes] = await Promise.all([
        fetch('/api/dataclassification/classifications'),
        fetch('/api/dataclassification/sensitive'),
        fetch('/api/dataclassification/statistics')
      ])
      if (classRes.ok) setClassifications(await classRes.json())
      if (sensitiveRes.ok) setSensitiveData(await sensitiveRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching data classification:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value)
  }

  const getSensitivityBadge = (sensitivity: string) => {
    if (sensitivity === 'Critical') return 'bg-red-100 text-red-800'
    if (sensitivity === 'High') return 'bg-orange-100 text-orange-800'
    if (sensitivity === 'Medium') return 'bg-yellow-100 text-yellow-800'
    return 'bg-green-100 text-green-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Data Classification</h1>
          <p className="mt-2 text-gray-600">Phase 266 - Sensitivity labeling and categorization</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Classified</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(statistics.total_classified)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Unclassified</p>
              <p className="text-2xl font-bold text-red-600">{statistics.unclassified}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Classification Rate</p>
              <p className="text-2xl font-bold text-green-600">{statistics.classification_rate}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">PII Datasets</p>
              <p className="text-2xl font-bold text-orange-600">{statistics.pii_datasets}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Sensitive</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.sensitive_datasets}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Violations</p>
              <p className="text-2xl font-bold text-red-600">{statistics.policy_violations}</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Classification Levels</h2>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-5 gap-4">
            {classifications.map((cls, idx) => (
              <div key={idx} className="p-4 rounded-lg border-2" style={{borderColor: cls.color}}>
                <div className="text-center mb-3">
                  <span className="text-lg font-bold" style={{color: cls.color}}>{cls.level}</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Datasets</span>
                    <span className="font-medium">{cls.datasets}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Tables</span>
                    <span className="font-medium">{cls.tables}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Columns</span>
                    <span className="font-medium">{formatNumber(cls.columns)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Sensitive Data Inventory</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dataset</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Column</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sensitivity</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Encrypted</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Masked</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sensitiveData.map((item) => (
                  <tr key={item.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{item.dataset}</td>
                    <td className="px-6 py-4 text-purple-600">{item.column}</td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">{item.type}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getSensitivityBadge(item.sensitivity)}`}>{item.sensitivity}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${item.encryption ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{item.encryption ? 'Yes' : 'No'}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${item.masking ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{item.masking ? 'Yes' : 'No'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  )
}
